import { useEffect } from "react";

export interface FaqItem {
  question: string;
  answer: string;
}

/**
 * Injects (and cleans up) a FAQPage JSON-LD script for a tool page's static FAQ content. `id` must be
 * unique per page so mounting one tool page never clobbers another's script tag.
 */
export function useFaqJsonLd(id: string, faq: FaqItem[]) {
  useEffect(() => {
    const elementId = `faq-jsonld-${id}`;
    if (faq.length === 0) {
      document.getElementById(elementId)?.remove();
      return;
    }

    let script = document.getElementById(elementId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = elementId;
      document.head.appendChild(script);
    }
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    });

    return () => { document.getElementById(elementId)?.remove(); };
  }, [id, faq]);
}
